import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Welcome back, ' + user.alias);
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'seller') navigate('/seller');
      else navigate('/buyer');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/buyer';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-white grid grid-cols-1 lg:grid-cols-2">
      {/* Left panel - form */}
      <div className="flex flex-col p-8 lg:p-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors" data-testid="login-back-link">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto">
            <div className="flex items-center gap-2 mb-8">
              <div className="w-8 h-8 bg-[#0055FF] flex items-center justify-center rounded-sm">
                <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <span className="font-heading text-xl font-semibold tracking-tight text-gray-900">
                LeadXchange
              </span>
            </div>

            <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">Sign in</h1>
            <p className="mt-2 text-sm text-gray-600">Welcome back. Continue trading with your verified alias.</p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  className="mt-1.5 h-11"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="mt-1.5 h-11"
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-[#0055FF] hover:bg-[#0044CC] text-white"
                data-testid="login-submit-btn"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
              <div className="text-right">
                <Link to="/forgot-password" className="text-xs text-gray-500 hover:text-[#0055FF]" data-testid="forgot-password-link">
                  Forgot your password?
                </Link>
              </div>
            </form>

            <div className="my-6 flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs uppercase tracking-wider text-gray-400">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              className="w-full h-11 border-gray-300 hover:bg-gray-50"
              data-testid="login-google-btn"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <p className="mt-8 text-sm text-gray-600 text-center">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="font-medium text-[#0055FF] hover:underline" data-testid="login-register-link">
                Get Started
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right panel - visual */}
      <div className="hidden lg:flex bg-gray-900 relative items-center justify-center overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="relative z-10 max-w-md px-12 text-white">
          <div className="text-xs uppercase tracking-[0.2em] text-blue-300 font-mono mb-6">
            Trading Protocol · v1.0
          </div>
          <h2 className="font-heading text-4xl font-semibold tracking-tight leading-tight">
            Australia's trusted marketplace<br/>
            for verified business leads.
          </h2>
          <div className="mt-12 space-y-4 font-mono text-sm">
            <div className="flex gap-3 items-center">
              <span className="text-blue-400">→</span>
              <span className="text-gray-300">LX-S-1042 listed Equipment Finance lead · $250</span>
            </div>
            <div className="flex gap-3 items-center">
              <span className="text-blue-400">→</span>
              <span className="text-gray-300">LX-B-2081 purchased lead LX-LEAD-A1B2C3</span>
            </div>
            <div className="flex gap-3 items-center">
              <span className="text-green-400">✓</span>
              <span className="text-gray-300">Settlement queued · 48h dispute window</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
